/**
 * Miro keyboard shortcuts on the Excalidraw board (shared keymap in
 * web/shared/src/keymap.ts). Our capture-phase listener runs before
 * Excalidraw's; keys we don't handle (copy, undo, group, ⌘D, ⌘F search,
 * ⌘⇧L lock, Enter, Esc…) fall through to Excalidraw, which already does what
 * Miro does.
 */
import { CaptureUpdateAction, getCommonBounds } from '@excalidraw/excalidraw';
import type { ExcalidrawElement } from '@excalidraw/excalidraw/element/types';
import type { AppState, ExcalidrawImperativeAPI } from '@excalidraw/excalidraw/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { KeymapOverlay, type OverlayMode } from '../../shared/src/components/KeymapOverlay';
import { arrowDirection, cycleIndex, detectPlatform, dispatchNativeKey, installKeymap, readingOrder, withKey, type CommandHandlers, type CommandId } from '../../shared/src/keymap';
import { toast } from './ui';

type Native = 'rectangle' | 'ellipse' | 'diamond' | 'arrow' | 'text' | 'eraser' | 'frame' | 'hand' | 'selection';
export type PlaceKind = 'card' | 'comment';
type ShapeStep = { kind: 'native'; tool: Native; label: string } | { kind: 'custom'; tool: 'triangle' | 'star'; label: string };

const SHAPE_CYCLE: ShapeStep[] = [
  { kind: 'native', tool: 'rectangle', label: 'Rectangle' },
  { kind: 'native', tool: 'ellipse', label: 'Oval' },
  { kind: 'native', tool: 'diamond', label: 'Diamond' },
  { kind: 'custom', tool: 'triangle', label: 'Triangle' },
  { kind: 'custom', tool: 'star', label: 'Star' },
];
const PAN_STEP = 80;
const DUP_GAP = 40;

export interface MiroKeysProps {
  xapi: ExcalidrawImperativeAPI;
  readOnly: boolean;
  /** Last pointer position in scene coordinates (null when off the canvas). */
  pointer: React.MutableRefObject<{ x: number; y: number } | null>;
  armTool(tool: 'sticky' | 'triangle' | 'star'): void;
  setPen(kind: 'pen' | 'highlighter'): void;
  place(kind: PlaceKind, x: number, y: number): void;
  onToggleMinimap(): void;
  onZoomToFit(): void;
  /** Sees every key first (the Zone tool while a zone is being drawn); true = consumed. */
  intercept?(e: KeyboardEvent): boolean;
  /** Design estimate: I arms the fixture tool, / focuses quick add, ⇧B toggles the panel, 1–9 picks a type while the tool is on. */
  estimate?: {
    arm(): void;
    quickAdd(): void;
    togglePanel(): void;
    /** Returns false (key passes through to Excalidraw) when the fixture tool is off. */
    favourite(slot: number): boolean;
    /** W / A: draw a run or an area measured on the drawing under it. */
    measure(kind: 'linear' | 'area'): void;
    /** ⇧C: two-click scale calibration of the selected (or pointed-at) drawing. */
    calibrate(): void;
    /** ⇧D: two-click dimension line labelled with its scaled length. */
    dimension(): void;
    /** ⇧M: mirror the selection across a two-click axis. */
    mirror(): void;
    /** Z: draw a ZONE (freehand drag or click points). */
    zone(): void;
    /** Esc while a board tool (mirror) is active: cancel it before Esc deselects. Returns true if it did. */
    cancelTool?(): boolean;
  };
}

const live = (xapi: ExcalidrawImperativeAPI) => xapi.getSceneElements().filter((e) => !e.isDeleted);
const selectedIds = (xapi: ExcalidrawImperativeAPI) => Object.keys(xapi.getAppState().selectedElementIds).filter((k) => xapi.getAppState().selectedElementIds[k]);
const host = () => document.querySelector('.xb-canvas .excalidraw') ?? document.body;
const modKey = () => (detectPlatform() === 'mac' ? { metaKey: true } : { ctrlKey: true });

/** Scene point at the viewport centre. */
function viewportCentre(st: AppState) {
  const z = st.zoom.value;
  return { x: st.width / 2 / z - st.scrollX, y: st.height / 2 / z - st.scrollY };
}

export function MiroKeysExcalidraw(p: MiroKeysProps) {
  const [overlay, setOverlay] = useState<OverlayMode | null>(null);
  const overlayRef = useRef(overlay);
  overlayRef.current = overlay;
  const shapeIdx = useRef(-1);
  const props = useRef(p);
  props.current = p;

  const handlers = useMemo<CommandHandlers>(() => {
    const { xapi } = props.current;
    const tool = (type: Native) => () => {
      xapi.setActiveTool({ type });
    };
    const shape = (i: number) => {
      shapeIdx.current = i;
      const s = SHAPE_CYCLE[i];
      if (s.kind === 'native') xapi.setActiveTool({ type: s.tool });
      else props.current.armTool(s.tool);
      return s;
    };
    const at = () => {
      const st = xapi.getAppState();
      const pt = props.current.pointer.current;
      const z = st.zoom.value;
      const onCanvas = pt && pt.x >= -st.scrollX && pt.y >= -st.scrollY && pt.x <= -st.scrollX + st.width / z && pt.y <= -st.scrollY + st.height / z;
      return onCanvas ? pt : viewportCentre(st);
    };
    const edit: CommandHandlers = {
      'tool.select': tool('selection'),
      'tool.text': tool('text'),
      'tool.sticky': () => props.current.armTool('sticky'),
      'tool.rectangle': () => void shape(0),
      'tool.oval': () => void shape(1),
      'tool.shapes': () => {
        const t = xapi.getAppState().activeTool;
        const onShape = (t.type === 'custom' && (t.customType === 'triangle' || t.customType === 'star')) || t.type === 'rectangle' || t.type === 'ellipse' || t.type === 'diamond';
        const s = shape(onShape ? (shapeIdx.current + 1) % SHAPE_CYCLE.length : Math.max(0, shapeIdx.current));
        toast(`${s.label} — press S again for the next shape`);
      },
      'tool.connector': tool('arrow'),
      'tool.pen': () => props.current.setPen('pen'),
      'tool.eraser': tool('eraser'),
      'tool.frame': tool('frame'),
      'tool.card': () => {
        const pt = at();
        props.current.place('card', pt.x, pt.y);
      },
      'tool.comment': () => {
        const pt = at();
        props.current.place('comment', pt.x, pt.y);
      },
      'obj.duplicateDirection': (_e, combo) => duplicateToward(xapi, combo),
      'obj.front': () => {
        if (!selectedIds(xapi).length) return false;
        dispatchNativeKey(host(), { key: ']', code: 'BracketRight', ...modKey(), ...(detectPlatform() === 'mac' ? { altKey: true } : { shiftKey: true }) });
      },
      'obj.back': () => {
        if (!selectedIds(xapi).length) return false;
        dispatchNativeKey(host(), { key: '[', code: 'BracketLeft', ...modKey(), ...(detectPlatform() === 'mac' ? { altKey: true } : { shiftKey: true }) });
      },
    };
    const view: CommandHandlers = {
      'tool.hand': tool('hand'),
      'view.minimap': () => props.current.onToggleMinimap(),
      'view.grid': () => {
        const st = xapi.getAppState();
        xapi.updateScene({ appState: { gridModeEnabled: !st.gridModeEnabled }, captureUpdate: CaptureUpdateAction.NEVER });
      },
      'app.palette': () => setOverlay('palette'),
      'app.shortcuts': () => setOverlay('shortcuts'),
      'nav.arrows': (e, combo) => {
        if (selectedIds(xapi).length) return false; // Excalidraw nudges the selection (Shift = bigger steps)
        const st = xapi.getAppState();
        const { dx, dy } = arrowDirection(combo);
        const step = (e.shiftKey ? PAN_STEP * 5 : PAN_STEP) / st.zoom.value;
        xapi.updateScene({ appState: { scrollX: st.scrollX - dx * step, scrollY: st.scrollY - dy * step }, captureUpdate: CaptureUpdateAction.NEVER });
      },
      'nav.zoom100': () => {
        const st = xapi.getAppState();
        const c = viewportCentre(st);
        xapi.updateScene({
          appState: { zoom: { value: 1 as AppState['zoom']['value'] }, scrollX: st.width / 2 - c.x, scrollY: st.height / 2 - c.y },
          captureUpdate: CaptureUpdateAction.NEVER,
        });
      },
      'nav.zoomFit': () => props.current.onZoomToFit(),
      'nav.zoomSelection': () => {
        const ids = new Set(selectedIds(xapi));
        const els = live(xapi).filter((e) => ids.has(e.id));
        if (!els.length) return false;
        xapi.scrollToContent(els, { fitToContent: true, animate: true });
      },
      // Miro: quit edit → switch to the cursor → deselect. While editing or
      // mid-gesture, Excalidraw's own Esc runs.
      'obj.escape': () => {
        if (props.current.estimate?.cancelTool?.()) return;
        const st = xapi.getAppState();
        if (st.editingTextElement || st.newElement || st.multiElement || st.editingLinearElement || st.openDialog) return false;
        if (st.activeTool.type !== 'selection') {
          xapi.setActiveTool({ type: 'selection' });
          return;
        }
        if (!selectedIds(xapi).length) return false;
        xapi.updateScene({ appState: { selectedElementIds: {}, selectedGroupIds: {} }, captureUpdate: CaptureUpdateAction.NEVER });
      },
      'obj.next': () => cycle(xapi, false),
      'obj.prev': () => cycle(xapi, true),
      'estimate.panel': () => props.current.estimate?.togglePanel(),
    };
    const estimate: CommandHandlers = {
      'estimate.fixture': () => props.current.estimate?.arm(),
      'estimate.quickAdd': () => props.current.estimate?.quickAdd(),
      'estimate.favourite': (_e, combo) => {
        const slot = Number(combo.slice(-1));
        return props.current.estimate?.favourite(slot) ?? false;
      },
      'estimate.linear': () => props.current.estimate?.measure('linear'),
      'estimate.area': () => props.current.estimate?.measure('area'),
      'estimate.calibrate': () => props.current.estimate?.calibrate(),
      'estimate.dimension': () => props.current.estimate?.dimension(),
      'estimate.mirror': () => props.current.estimate?.mirror(),
      'estimate.zone': () => props.current.estimate?.zone(),
    };
    return props.current.readOnly ? view : { ...view, ...edit, ...estimate };
  }, [p.xapi, p.readOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => installKeymap(handlers, { enabled: () => overlayRef.current === null, intercept: (e) => props.current.intercept?.(e) ?? false }), [handlers]);
  useToolTitles();

  if (!overlay) return null;
  return (
    <KeymapOverlay
      mode={overlay}
      onClose={() => setOverlay(null)}
      available={(id: CommandId) => !!handlers[id]}
      onRun={(id) => {
        const h = handlers[id];
        if (h) h(new KeyboardEvent('keydown'), '');
      }}
      notes={{
        'tool.card': 'A card-styled box (Excalidraw has no card type)',
        'tool.comment': 'A small sticky-style comment pin',
        'nav.search': "Excalidraw's own search panel",
        'view.minimap': 'Small overview, bottom right',
      }}
    />
  );
}

function cycle(xapi: ExcalidrawImperativeAPI, backwards: boolean) {
  const els = live(xapi).filter((e) => e.type !== 'arrow' && !(e.type === 'text' && e.containerId));
  const order = readingOrder(els.map((e) => ({ id: e.id, x: e.x, y: e.y, h: e.height })));
  const sel = selectedIds(xapi);
  const i = cycleIndex(order.length, order.findIndex((o) => o.id === sel[0]), backwards);
  if (i < 0) return;
  const id = order[i].id;
  xapi.updateScene({ appState: { selectedElementIds: { [id]: true } as AppState['selectedElementIds'] }, captureUpdate: CaptureUpdateAction.NEVER });
  const el = live(xapi).find((e) => e.id === id);
  if (el && !inView(xapi.getAppState(), el)) xapi.scrollToContent(el, { animate: true });
}

function inView(st: AppState, el: ExcalidrawElement) {
  const z = st.zoom.value;
  const x0 = -st.scrollX;
  const y0 = -st.scrollY;
  return el.x >= x0 && el.y >= y0 && el.x + el.width <= x0 + st.width / z && el.y + el.height <= y0 + st.height / z;
}

/**
 * ⌥+arrow: Excalidraw's own duplicate (keeps bound text, groups and
 * arrow bindings), then slide the copies next to the originals.
 */
function duplicateToward(xapi: ExcalidrawImperativeAPI, combo: string): boolean | void {
  const sel = new Set(selectedIds(xapi));
  const originals = live(xapi).filter((e) => sel.has(e.id));
  if (!originals.length) return false;
  const [x1, y1, x2, y2] = getCommonBounds(originals);
  const { dx, dy } = arrowDirection(combo);
  const target = { x: x1 + dx * (x2 - x1 + DUP_GAP), y: y1 + dy * (y2 - y1 + DUP_GAP) };
  dispatchNativeKey(host(), { key: 'd', code: 'KeyD', ...modKey() });
  requestAnimationFrame(() => {
    const now = new Set(selectedIds(xapi));
    if (!now.size || [...now].every((id) => sel.has(id))) return;
    const copies = live(xapi).filter((e) => now.has(e.id));
    const bound = new Set(copies.flatMap((e) => (e.boundElements ?? []).filter((b) => b.type === 'text').map((b) => b.id)));
    const [cx, cy] = getCommonBounds(copies);
    const ox = target.x - cx;
    const oy = target.y - cy;
    const moved = xapi.getSceneElementsIncludingDeleted().map((e) =>
      now.has(e.id) || bound.has(e.id) ? { ...e, x: e.x + ox, y: e.y + oy, version: e.version + 1, versionNonce: Math.floor(Math.random() * 2 ** 31), updated: Date.now() } : e,
    );
    xapi.updateScene({ elements: moved, captureUpdate: CaptureUpdateAction.IMMEDIATELY });
  });
}

/** Excalidraw's toolbar titles say "Line — L or 6"; show the Miro keys instead. */
const TITLES: Record<string, [string, CommandId | null]> = {
  selection: ['Select', 'tool.select'],
  hand: ['Hand', 'tool.hand'],
  rectangle: ['Rectangle', 'tool.rectangle'],
  diamond: ['Diamond (S cycles shapes)', null],
  ellipse: ['Oval', 'tool.oval'],
  arrow: ['Connection line', 'tool.connector'],
  line: ['Line', null],
  freedraw: ['Pen', 'tool.pen'],
  text: ['Text', 'tool.text'],
  eraser: ['Eraser', 'tool.eraser'],
  frame: ['Frame', 'tool.frame'],
};

function useToolTitles() {
  useEffect(() => {
    const apply = () => {
      for (const [id, [label, cmd]] of Object.entries(TITLES)) {
        const input = document.querySelector<HTMLElement>(`[data-testid="toolbar-${id}"]`);
        const label_ = input?.closest('label') ?? input;
        if (!label_) continue;
        const title = cmd ? withKey(label, cmd) : label;
        if (label_.getAttribute('title') !== title) label_.setAttribute('title', title);
        if (input && input.getAttribute('title') !== title) input.setAttribute('title', title);
      }
    };
    apply();
    const root = document.querySelector('.xb-canvas') ?? document.body;
    const mo = new MutationObserver(apply);
    mo.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['title'] });
    return () => mo.disconnect();
  }, []);
}
